import React from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import Portal from '../general-components/portal';
import { MenuItem } from '../general-components/right-click-menu';

interface GraphicMenuItemProps {
	id: string;
	item: MenuItem;
	multipleItems: boolean;
	subMenuPortalTargetId?: string;
	hoveredItemKey?: string | null;
	setHoveredItemKey?: React.Dispatch<React.SetStateAction<string | null>>;
	isSubMenuItem?: boolean;
}

// This component was extracted from the RightClickMenu
export default function GraphicMenuItem({
	id,
	item,
	multipleItems,
	subMenuPortalTargetId,
	hoveredItemKey,
	setHoveredItemKey,
	isSubMenuItem = false,
}: GraphicMenuItemProps) {
	const {
		checkboxValue,
		CustomSubMenu,
		disabled,
		handleClick,
		iconName,
		iconSize,
		subMenuItems,
		text,
	} = item;
	const dispatch = useDispatch();

	const hasSubMenu = !!subMenuItems?.length || CustomSubMenu;

	// TODO: COLORS is icon colors; #FF5F7F is hardcoded in other files as well to match icon red
	const iconColor = item.color || COLORS.WHITE;
	const color = item.color === COLORS.RED
		? '#FF5F7F'
		: item.color
		|| COLORS.WHITE;

	const checkboxOrIcon = () => {
		if (typeof checkboxValue === 'boolean') {
			return (
				<div
					className={classNames('graphic-menu-checkbox', {
						selected: checkboxValue,
					})}
				>
					<div className="checkbox-outer">
						<Icon name={ICONS.CHECK} size={10} color={iconColor} />
					</div>
				</div>
			);
		}

		if (iconName) {
			return (
				<div className="graphic-menu-icon">
					<Icon name={iconName} size={iconSize || 12} color={iconColor} />
				</div>
			);
		}

		return null;
	};

	const isActive = hoveredItemKey === id && !disabled && !isSubMenuItem && hasSubMenu;

	return (
		<div
			className={classNames(
				'graphic-menu-option',
				'graphic-menu-row',
				multipleItems ? 'multiple-items' : 'only-item',
				{ 
					disabled,
					'graphic-menu-option-active': isActive,
				},
			)}
			onMouseEnter={() => {
				if (!isSubMenuItem && setHoveredItemKey) {
					setHoveredItemKey(id);
				}
			}}
			onClick={() => {
				if (handleClick && !hasSubMenu && !disabled) {
					handleClick();
				}

				// Check if the menu shouldn't close on click
				if (!item.dontClose) {
					dispatch(setOpenContextMenuId(null));
				}
			}}
		>
			{checkboxOrIcon()}
			<div
				className={`graphic-menu-text${hasSubMenu ? ' open-sub' : ''} bl-font-p2`}
				style={{ color }}
			>
				{text}
				{hasSubMenu && (
					<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} color={iconColor} />
				)}
			</div>
			{hoveredItemKey === id && hasSubMenu && (
				<>
					{(CustomSubMenu && subMenuPortalTargetId) ? (
						<Portal targetId={subMenuPortalTargetId}>
							{CustomSubMenu}
						</Portal>
					) : (
						<div className={'graphic-menu sub-menu'}>
							{subMenuItems?.map((subItem, i) => {
								const key = `${id}-sub-${i}`;

								return (
									<GraphicMenuItem
										key={key}
										id={key}
										item={subItem}
										multipleItems={subMenuItems.length > 1}
										subMenuPortalTargetId={subMenuPortalTargetId}
										isSubMenuItem
									/>
								);
							})}
						</div>
					)}
				</>
			)}
		</div>
	);
}
